/**
 * Selling plan cards — subscribe vs one-time cards (snippets/component-selling-plan-cards.liquid).
 *
 * The radios are the product form's `selling_plan` field, so the cart engine
 * picks the choice up from FormData with no extra wiring. This element only
 * makes the whole card clickable, handles the "switch to subscription" link
 * and mirrors the selected price into `[data-purchase-price]` in the same form.
 * The `toggle` layout has one switch instead of radios; its price follows the
 * switch's checked state.
 *
 * Docs: docs/snippets/component-selling-plan-cards.md
 * @extends HTMLElement
 */
export class SellingPlanCards extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.updatePrice();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
  }

  get selectedInput() {
    return this.querySelector('.selling-plan-cards__input:checked');
  }

  /**
   * @param {MouseEvent} event
   */
  onClick = (event) => {
    if (event.target.closest('[data-selling-plan-cards-subscribe]')) {
      const toggle = this.querySelector('.selling-plan-cards__toggle-input');
      this.select(toggle ?? this.querySelector('.selling-plan-cards__input:not([value=""])'));
      // The link hides with the one-time state, so focus moves to the switch it turned on
      toggle?.focus({ preventScroll: true });
      return;
    }

    // Labels and the radio itself already toggle natively
    if (event.target.closest('label, input')) return;

    const option = event.target.closest('.selling-plan-cards__option');
    if (option) this.select(option.querySelector('.selling-plan-cards__input'));
  };

  onChange = () => {
    this.updatePrice();
  };

  /**
   * @param {HTMLInputElement | null} input
   */
  select(input) {
    if (!input || input.checked) return;

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  updatePrice() {
    const toggle = this.querySelector('.selling-plan-cards__toggle-input');
    const price = toggle ? toggle.dataset[toggle.checked ? 'price' : 'priceOff'] : this.selectedInput?.dataset.price;
    const form = this.closest('form');
    if (!price || !form) return;

    // Buttons outside the form (e.g. a sticky bar) join it with the `form` attribute. getAttribute, not
    // form.id: a product form holds an input named "id", which shadows the property.
    const formId = form.getAttribute('id');
    const outside = formId ? document.querySelectorAll(`[form="${formId}"] [data-purchase-price]`) : [];
    for (const element of [...form.querySelectorAll('[data-purchase-price]'), ...outside]) {
      element.textContent = price;
    }
  }
}

if (!customElements.get('selling-plan-cards')) {
  customElements.define('selling-plan-cards', SellingPlanCards);
}
